import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  TextField,
  MenuItem,
  Typography,
  Grid,
  Paper,
  Divider,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Icon,
  Chip,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Select,
  FormHelperText
} from "@material-ui/core";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import { DropzoneArea } from "material-ui-dropzone";
import Button from "components/CustomButtons/Button.js";
import { createProjectAPI } from "views/APIs/ProjectsUtility";


export async function fetchImageAsFile(imageUrl) {
  const response = await fetch(imageUrl);
  const blob = await response.blob();
  const file = new File([blob], "qcreate_logo.png", { type: blob.type });
  return file;
}


const CREATE_SENTINEL = "__create_new_project__";
const images = require.context("assets/img/create_cards_16_9/", true);



const ProjectDetails = ({
  project = {
    name: "Model Validation to Form Builder",
    description: "This project is used to validate the model and convert it to a form builder.",
    associated_project_id: "",
    file: { name: "" },
    linked_project_id: ""
  },
  associatedProjectsFromParent,
  devValProjectsFromParent,
  onSave = () => { },
  onNext = () => { },
  loading = false,
  setFileChanged = () => { }
}) => {
  const [formData, setFormData] = useState({
    name: project.name || "",
    description: project.description || "",
    associated_project_id: project.associated_project_id || "",
    file: project.file?.name === "" ? null : project.file,
    linked_project_id: project.linked_project_id || ""
  });

  const hasSource = useMemo(() => {
    return !!(formData?.file || formData?.linked_project_id);
  }, [formData?.file, formData?.linked_project_id]);
  const [associatedProjects, setAssociatedProjects] = useState([
    ...associatedProjectsFromParent,
    { id: CREATE_SENTINEL, name: "+ Create new project" },
  ]);

  const [devValProjects, setDevValProjects] = useState([
    ...devValProjectsFromParent,
  ]);

  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [newDesc, setNewDesc] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAssociatedProjectChange = (e) => {
    const val = e.target.value;
    if (val === CREATE_SENTINEL) {
      // open modal; don’t change selection yet
      setCreateOpen(true);
      return;
    }
    setFormData((prev) => ({ ...prev, associated_project_id: val }));
  };
  const [mode, setMode] = useState(formData?.linked_project_id ? "existing" : "upload");

  const hasValue = useMemo(
    () => (mode === "existing" ? !!formData?.linked_project_id : !!formData?.file),
    [mode, formData?.linked_project_id, formData?.file]
  );

  const showError = !hasValue; // tweak this if you have a form-level touched/submit flag

  const onModeChange = (e) => {
    const next = e.target.value;
    setMode(next);
    // clear the opposite field to enforce either-or
    setFormData((prev) => ({
      ...prev,
      linked_project_id: next === "existing" ? prev.linked_project_id ?? "" : "",
      file: next === "upload" ? prev.file ?? null : null,
    }));
  };

  const onPlanChange = (e, child) => {
    // capture synchronously to avoid pooling issues
    const value = e?.target?.value ?? child?.props?.value ?? "";
    if (!value) return; // ignore the placeholder
    setFormData(prev => ({ ...prev, linked_project_id: value, file: null }));
  };

  const createNewProject = async (name, description) => {

    try {
      // Prepare form data
      const formData = new FormData();
      formData.append("project_name", name);
      formData.append("project_description", description);

      // Create course via API call
      const data = await createProjectAPI(formData);
      return data;
    } catch (error) {
    }
  };

  const handleCreate = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    try {
      const created = await createNewProject(newName.trim(), newDesc.trim());
      // add to list and select it
      const sentinelPopped = associatedProjects.pop();
      setAssociatedProjects([...associatedProjects, { id: created._id, name: created.course_name }, sentinelPopped]);
      setFormData((prev) => ({ ...prev, associated_project_id: created._id }));
      // reset and close
      setNewName("");
      setNewDesc("");
      setCreateOpen(false);
    } finally {
      setCreating(false);
    }
  };

  const handleClose = () => {
    setCreateOpen(false);
    setNewName("");
    setNewDesc("");
  };

  const handleFileChange = (files) => {
    if (files && files.length > 0) {
      setFileChanged(true);
      setFormData(prev => ({
        ...prev,
        file: files[0]
      }));
    }
  };

  const handleRemoveFile = () => {
    setFormData(prev => ({
      ...prev,
      file: null
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  const handleSave = () => {
    onNext(formData);
  };

  return (
    <Box component="form">
      <Typography variant="h5">
        Workflow Overview
      </Typography>
      <Typography variant="body1">
        This workflow guides you through transforming a document into a structured, interactive form. <br />
        It enables you to publish the template for external use and seamlessly capture, manage, and record submitted entries for ongoing review and analysis.
      </Typography>
      <Divider style={{ marginTop: "16px", marginBottom: "16px" }} />

      <Typography variant="h6" style={{ textTransform: "none" }}>
        Project Details
      </Typography>
      <Typography variant="body1">
        Edit the project details below.
      </Typography>
      <Divider style={{ marginTop: "16px", marginBottom: "16px" }} />

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Project Name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            margin="normal"
            required
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            select
            fullWidth
            label="Associated Project"
            name="associated_project_id"
            value={formData.associated_project_id}
            onChange={handleAssociatedProjectChange}
            margin="normal"
            required
          >
            {associatedProjects.map((project) => (
              <MenuItem key={project.id} value={project.id}>
                {project.name}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        {/* Create Project Modal */}
        <Dialog open={createOpen} onClose={!creating ? handleClose : undefined} fullWidth maxWidth="sm">
          <DialogTitle>Create a new project</DialogTitle>
          <DialogContent dividers>
            <TextField
              fullWidth
              label="Project name"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              autoFocus
              margin="dense"
              required
            />
            <TextField
              fullWidth
              label="Description"
              value={newDesc}
              onChange={(e) => setNewDesc(e.target.value)}
              margin="dense"
              multiline
              minRows={3}
            />
            <Typography variant="caption" color="textSecondary">
              A new project will be created and selected as the Associated Project.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} disabled={creating}>
              Cancel
            </Button>
            <Button
              color="quSandBoxColor"
              variant="contained"
              onClick={handleCreate}
              disabled={creating || !newName.trim()}
            >
              {creating ? "Creating…" : "Create"}
            </Button>
          </DialogActions>
        </Dialog>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            margin="normal"
            multiline
            rows={4}
          />
        </Grid>

        <Grid item xs={12}>
          <FormControl component="fieldset" sx={{ width: "100%" }} error={showError}>
            <FormLabel component="legend">Choose the source for the template:*</FormLabel>
            <RadioGroup
              row
              name="plan-or-upload"
              value={mode}
              onChange={onModeChange}
              sx={{ mt: 1 }}
              color="quSandBoxColor"
            >
              <FormControlLabel value="existing" control={<Radio />} label="Use existing plan" />
              <FormControlLabel value="upload" control={<Radio />} label="Upload file (.md or .pdf)" />
            </RadioGroup>

            {/* EXISTING PLAN MODE */}
            {mode === "existing" && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption">Pick a plan to fetch and convert into a form.</Typography>
                <Select
                  fullWidth
                  name="linked_project_id"
                  value={formData?.linked_project_id ?? ""}
                  onChange={onPlanChange}
                  displayEmpty
                  renderValue={(val) =>
                    val ? (devValProjects.find(p => p._id === val)?.name ?? val) : "Select a plan…"
                  }
                >
                  <MenuItem disabled value=""><em>Select a plan…</em></MenuItem>
                  {devValProjects.map((p) => (
                    <MenuItem key={p._id} value={p._id}>{p.name}</MenuItem>
                  ))}
                </Select>
                {showError && <FormHelperText>Please select a plan.</FormHelperText>}
              </Box>
            )}

            {/* UPLOAD MODE */}
            {mode === "upload" && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption">
                  Upload a .md or .pdf file to convert it into a form. Markdown files are preferred. *
                </Typography>
                <br />
                {formData.file ? (
                  <Chip
                    icon={<Icon fontSize="small">file</Icon>}
                    deleteIcon={<Icon fontSize="small">delete</Icon>}
                    onDelete={handleRemoveFile}
                    sx={{ borderRadius: "9999px", maxWidth: "100%" }}
                    label={
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, overflow: "hidden" }}>
                        <Typography variant="body2" noWrap sx={{ maxWidth: { xs: "40vw", sm: 320 } }}>
                          {formData.file?.name}
                        </Typography>
                      </Box>
                    }
                  />
                ) : (
                  <DropzoneArea
                    onChange={(files) => {
                      // keep modes mutually exclusive
                      setFormData((prev) => ({ ...prev, linked_project_id: "", file: files?.[0] || null }));
                      handleFileChange(files);
                    }}
                    filesLimit={1}
                    maxFileSize={50_000_000}
                    showAlerts={false}
                    dropzoneText="Drag and drop a file here or click"
                    acceptedFiles={[
                      ".md",
                      ".markdown",
                      "text/markdown",
                      "text/x-markdown",
                      "text/plain",
                      ".pdf",
                    ]}
                    Icon={CloudUploadIcon}
                    showPreviewsInDropzone={false}
                    showFileNames
                    className="dropzone"
                    style={{ height: "100px", maxHeight: "100px" }}
                  />
                )}
                {showError && <FormHelperText>Please upload a file.</FormHelperText>}
              </Box>
            )}
          </FormControl>
        </Grid>
      </Grid>

      <div style={{
        display: 'flex',
        justifyContent: 'flex-end',
        marginLeft: 'auto'
      }}>
        <Box sx={{ marginRight: '10px' }}>
          <Button
            color="quSandBoxColor"
            onClick={handleSubmit}
            disabled={loading || !hasSource}
          >
            {loading ? <CircularProgress size={24} /> : 'Update'}
          </Button>
          <Button
            color="quSandBoxColor"
            variant="contained"
            onClick={handleSave}
            disabled={loading || !hasSource}
          >
            Next
          </Button>
        </Box>
      </div>
    </Box>
  );
};


export default ProjectDetails;
